"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const turf = __importStar(require("@turf/turf"));
const polyline_1 = __importDefault(require("@mapbox/polyline"));
const RoutingService = __importStar(require("./RoutingService"));
const ServiceLocator_1 = __importDefault(require("../../utils/ServiceLocator"));
const LodashExtensions_1 = require("../../utils/LodashExtensions");
const Preferences_1 = __importDefault(require("../../config/Preferences"));
const Status = __importStar(require("../../utils/Status"));
const MathUtils_1 = require("../../utils/MathUtils");
const defaultRoutingRadiusMeters = 20;
const osrmStepToResult = (step) => {
    return {
        distance: step.distance,
        duration: step.duration,
        geometry: step.geometry.type
            ? step.geometry
            : polyline_1.default.decode(step.geometry)
    };
};
const osrmLegToResult = (leg) => {
    return {
        distance: leg.distance,
        duration: leg.duration,
        steps: leg.steps.map(osrmStepToResult)
    };
};
const osrmMatchingToResult = (matching) => {
    return Object.assign(Object.assign({}, matching), { legs: matching.legs.map(osrmLegToResult) });
};
const osrmRouteToResult = (matching) => {
    return {
        distance: matching.distance,
        duration: matching.duration,
        geometry: matching.geometry,
        legs: matching.legs.map(osrmLegToResult)
    };
};
/**
 * This class provides routing services using the OSRM backend
 *
 * FIXME: Clarify the map match algorithm, the code here does not work, somewhat
 * copy-pasted from updateGeography
 */
class OSRMRoutingService extends RoutingService.default {
    processMapMatchingResult(routingResult) {
        const osrmResult = Status.unwrap(routingResult);
        return {
            tracepoints: osrmResult.tracepoints
                ? osrmResult.tracepoints.map((tp) => (tp ? turf.point(tp.location).geometry : null))
                : [],
            matchings: osrmResult.matchings.map(osrmMatchingToResult)
        };
    }
    processRoutingResult(routingResult) {
        const osrmResult = Status.unwrap(routingResult);
        const filteredResult = this.filterNetworkTooFar(osrmResult);
        return {
            waypoints: filteredResult.waypoints
                ? filteredResult.waypoints.map((tp) => (tp ? turf.point(tp.location).geometry : null))
                : [],
            routes: filteredResult.routes.map(osrmRouteToResult)
        };
    }
    // If origin or destination are too far from the network, invalidate the results
    filterNetworkTooFar(routingResult) {
        const waypoints = routingResult.waypoints;
        if (waypoints) {
            const origin = waypoints[0];
            const destination = waypoints[waypoints.length - 1];
            // TODO: Migrate this value from preferences to config like the osrm modes. See issue #1140
            const maxDistance = Preferences_1.default.get('osrmRouting.maxDistanceFromNearestNetworkNodeMeters');
            if (origin.distance > maxDistance || destination.distance > maxDistance) {
                //TODO Should we throw an exception instead here?
                routingResult.routes[0].distance = -1;
                routingResult.routes[0].duration = -1;
            }
        }
        return routingResult;
    }
    callOsrmMap(params) {
        return __awaiter(this, void 0, void 0, function* () {
            //console.log("osrm params", params);
            return new Promise((resolve) => {
                ServiceLocator_1.default.socketEventManager.emit('service.osrmRouting.match', params, (routingResult) => {
                    //console.log('routingResult', routingResult);
                    resolve(routingResult);
                });
            });
        });
    }
    callOsrmRoute(params) {
        return __awaiter(this, void 0, void 0, function* () {
            //console.log("osrm params", params);
            return new Promise((resolve) => {
                ServiceLocator_1.default.socketEventManager.emit('service.osrmRouting.route', params, (routingResult) => {
                    //console.log('routingResult', routingResult);
                    resolve(routingResult);
                });
            });
        });
    }
    callOsrmTableFrom(params) {
        return __awaiter(this, void 0, void 0, function* () {
            //console.log("osrm params", params);
            return new Promise((resolve) => {
                ServiceLocator_1.default.socketEventManager.emit('service.osrmRouting.tableFrom', params, (tableResult) => {
                    //console.log('routingResult', routingResult);
                    resolve(tableResult);
                });
            });
        });
    }
    callOsrmTableTo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            //console.log("osrm params", params);
            return new Promise((resolve) => {
                ServiceLocator_1.default.socketEventManager.emit('service.osrmRouting.tableTo', params, (tableResult) => {
                    //console.log('routingResult', routingResult);
                    resolve(tableResult);
                });
            });
        });
    }
    mapMatch(params) {
        return __awaiter(this, void 0, void 0, function* () {
            // we need to divide radiuses by 3 since osrm match service will use RADIUS_MULTIPLIER of 3.
            const radiuses = params.points.features.map((feature) => { var _a; return (0, MathUtils_1.ceilToPositiveInteger)((((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.radius) || defaultRoutingRadiusMeters) / 3); });
            const timestamps = params.points.features.map((feature) => { var _a; return ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.timestamp) || 0; });
            const routingResult = yield this.callOsrmMap({
                mode: params.mode,
                points: params.points.features,
                radiuses: radiuses,
                timestamps: timestamps,
                steps: params.showSteps || true
                // gaps      : 'ignore',
                // continue_straight: Preferences.current.osrmRouting.useContinueStraightForMapMatching === true ? true : undefined // see comment in chaire-lib-common/lib/config/defaultPreferences.config.js
            });
            return this.processMapMatchingResult(routingResult);
        });
    }
    route(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const routingResult = yield this.callOsrmRoute({
                mode: params.mode,
                points: params.points.features,
                steps: params.showSteps || false,
                overview: params.overview === 'simplified' ? 'simplified' : params.overview === 'false' ? 'false' : 'full',
                annotations: (0, LodashExtensions_1._isBlank)(params.annotations) ? false : params.annotations,
                // gaps      : 'ignore',
                continue_straight: 
                // TODO: Migrate this value from preferences to config like the osrm modes. See issue #1140
                Preferences_1.default.current.osrmRouting.useContinueStraightForMapMatching === true ? true : undefined, // see comment in chaire-lib-common/lib/config/defaultPreferences.config.js
                withAlternatives: params.withAlternatives === true ? true : false
            });
            return this.processRoutingResult(routingResult);
        });
    }
    tableFrom(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const routingResult = yield this.callOsrmTableFrom(Object.assign({}, params));
            return Status.unwrap(routingResult);
        });
    }
    tableTo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const routingResult = yield this.callOsrmTableTo(Object.assign({}, params));
            return Status.unwrap(routingResult);
        });
    }
}
exports.default = OSRMRoutingService;
//# sourceMappingURL=OSRMRoutingService.js.map